import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { DomainEntityBase, DomainEntityType } from '@models';
import {
  EntityCollectionServiceBase,
  EntityCollectionServiceElementsFactory,
} from '@ngrx/data';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  take,
} from 'rxjs';
import { routeSelectors } from './stores';

@Injectable()
export class DataCollectionService<
  T extends DomainEntityBase
> extends EntityCollectionServiceBase<T> {
  constructor(
    entityName: DomainEntityType,
    elementsFactory: EntityCollectionServiceElementsFactory
  ) {
    super(entityName.toString(), elementsFactory);
  }

  public override get store(): Store<any> {
    return super.store as Store<any>;
  }

  getByKeyFromPathParam(pathParamName: string): Observable<T | null> {
    this.store
      .select(routeSelectors.selectRouteParam(pathParamName))
      .pipe(
        take(1),
        map((id) => (typeof id === 'string' ? +id : id)),
        mergeMap((id) => {
          if (!id) {
            return of(null);
          }

          return this.getByKey(id);
        })
      )
      .subscribe();

    return this.selectByKeyFromPathParam(pathParamName);
  }

  selectByKeyFromPathParam(pathParamName: string): Observable<T> {
    return this.store.select(routeSelectors.selectRouteParams).pipe(
      map((params: Params) => params[pathParamName]),
      distinctUntilChanged(),
      map((key) => +key),
      mergeMap((key) =>
        this.entityMap$.pipe(
          filter((entityMap) => !key || !!entityMap[key]),
          map((entities) => entities[key] as T)
        )
      )
    );
  }

  selectByKey(key: number): Observable<T> {
    return this.entityMap$.pipe(
      filter((entityMap) => !key || !!entityMap[key]),
      map((entityMap) => entityMap[key] as T)
    );
  }

  save(entity: T): Observable<T> {
    if (entity.id) {
      return this.update(entity);
    } else {
      return this.add(entity);
    }
  }
}
